import { useTranslation } from "react-i18next";

import { useSetAtom } from "jotai";

import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";

import { userAtom } from "@/atoms/auth.atoms";

import authService from "@/services/auth.service";

interface SessionExpiredModalProps {
  open: boolean;
}

export function SessionExpiredModal({ open }: SessionExpiredModalProps) {
  const { t } = useTranslation("common");

  const navigate = useNavigate();

  const setUser = useSetAtom(userAtom);

  if (!open) return null;

  const handleLogin = async () => {
    // Ask backend to wipe eriol_session + eriol_csrf cookies (best-effort —
    // if it 401s because the session is already gone, the local state below
    // still gets cleared).
    try {
      await authService.logout();
    } catch {
      /* ignore */
    }

    setUser(null);

    navigate("/login", { replace: true });
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("sessionExpiredTitle")}
        className="w-full max-w-sm mx-4 rounded-xl border border-white/[0.08] bg-card p-6 shadow-2xl animate-[fadeIn_0.2s_ease-out]"
      >
        <div className="flex flex-col items-center gap-4 text-center">
          {/* Lock icon */}
          <div className="w-14 h-14 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-destructive"
            >
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>

          <div className="space-y-1.5">
            <h2 className="text-lg font-semibold text-foreground">
              {t("sessionExpiredTitle")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("sessionExpiredMessage")}
            </p>
          </div>

          <Button onClick={handleLogin} className="w-full mt-2 min-h-11">
            {t("sessionExpiredAction")}
          </Button>
        </div>
      </div>
    </div>
  );
}
